import {
  createElement,
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import { renderToStaticMarkup } from "react-dom/server";
import {
  getAllConnectedNodeEdgesTypes,
  getGraphEdges,
  getGlobalGraph,
  getTeamMemberGraph,
} from "../../../api/graph";
import { getDocIdData } from "../../../api/universal";
import ForceGraph2D from "react-force-graph-2d";
import * as d3 from "d3";
import {
  Loader2,
  Network,
  FileText,
  Clock,
  UserCheck,
  Briefcase,
  User,
  Link2,
  MapPin,
  GraduationCap,
  Building2,
  Search,
  ShieldAlert,
  Building,
  GitCompareArrows,
  Globe,
  Ruler,
  Factory,
  Ship,
  Package,
  Info,
  Coins,
  BookOpen,
  Scale,
  Hash,
  ShieldCheck,
  Award,
  CalendarDays,
  Fingerprint,
  X,
} from "lucide-react";
import { useRightSidebar } from "../../contexts/RightSidebarContext";
import ArticleDetailPanel from "../../Universal/ArticleCardDetailPanel";
import { getTheme } from "../../Universal/useNavTheme";

// Backend icon type → Lucide component.
const VELBLUE = "#0EA5E9";

const ICON_MAP = {
  DOCUMENT: FileText,
  DATETIME: Clock,
  DATE: CalendarDays,
  EMPLOYEE: UserCheck,
  OCCUPATION: Briefcase,
  PERSON: User,
  URL: Link2,
  LOCATION: MapPin,
  SCHOOL: GraduationCap,
  WORKED_AT: Building2,
  SANCTION_LOOKUP_NAME: Search,
  SANCTION_ENTITY: ShieldAlert,
  COMPANY: Building,
  SIMILAR: GitCompareArrows,
  WEBSITE: Globe,
  SIZE: Ruler,
  INDUSTRY: Factory,
  BILL_OF_LADING: Ship,
  BoL: Ship,
  CONTAINER: Package,
  CRYPTO: Coins,
  LEDGER: BookOpen,
  COURT_FILING: Scale,
  DOCKET_NUMBER: Hash,
  DOMAIN_CERTIFICATE: ShieldCheck,
  CERTIFICATE: Award,
  SERIAL_NUMBER: Fingerprint,
};

const FALLBACK_ICON = Info;

// Layout constants — keep these in sync: linkDistance must be >= 2 * maxCollideRadius
// or the simulation can never reach equilibrium and nodes overlap.
const MAX_LABEL_CHARS = 28;
const COLLIDE_PER_CHAR = 3.5;
const COLLIDE_PADDING = 20;
const COLLIDE_MIN = 65;
const MAX_COLLIDE_RADIUS = MAX_LABEL_CHARS * COLLIDE_PER_CHAR + COLLIDE_PADDING;
const LINK_DISTANCE = Math.max(260, MAX_COLLIDE_RADIUS * 2.2);

const ICON_PX = 96;

function lucideToImage(IconComponent, color) {
  const markup = renderToStaticMarkup(
    createElement(IconComponent, {
      size: ICON_PX,
      color,
      strokeWidth: 2.5,
    }),
  );
  const img = new Image();
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
  return img;
}

function buildIconCache() {
  const cache = {};
  for (const [key, Component] of Object.entries(ICON_MAP)) {
    cache[key] = lucideToImage(Component, VELBLUE);
  }
  cache._DEFAULT = lucideToImage(FALLBACK_ICON, VELBLUE);
  return cache;
}

const VB = VELBLUE;

function getCanvasTheme(isDark) {
  if (isDark) {
    return {
      nodeFill: "rgba(18,28,46,0.92)",
      nodeStroke: `rgba(14,165,233,0.30)`,
      nodeHoverFill: `rgba(14,165,233,0.22)`,
      nodeHoverStroke: `rgba(14,165,233,0.80)`,
      labelBg: "rgba(10,16,28,0.94)",
      labelStroke: `rgba(14,165,233,0.15)`,
      labelText: "rgba(160,190,220,0.85)",
      labelHoverText: "rgba(220,235,250,1)",
      link: `rgba(14,165,233,0.35)`,
      linkHover: `rgba(14,165,233,0.75)`,
      particle: VB,
      velBlue: VB,
    };
  }

  return {
    nodeFill: "rgba(185,195,212,0.70)",
    nodeStroke: "rgba(110,128,152,0.50)",
    nodeHoverFill: "rgba(185,195,212,0.90)",
    nodeHoverStroke: `rgba(14,165,233,0.60)`,
    labelBg: "rgba(255,255,255,0.92)",
    labelStroke: "rgba(130,145,168,0.40)",
    labelText: "rgba(30,45,65,0.80)",
    labelHoverText: "rgba(15,23,42,0.95)",
    link: "rgba(80,100,125,0.35)",
    linkHover: "rgba(40,55,75,0.60)",
    particle: "rgba(90,110,135,0.6)",
    velBlue: VB,
  };
}

function buildNodes(raw) {
  const seen = new Set();
  const out = [];
  for (const n of raw) {
    if (seen.has(n.name)) continue;
    seen.add(n.name);
    out.push({
      id: n.name,
      name: n.name,
      group: n.group,
      iconType: ICON_MAP[n.icon] ? n.icon : "_DEFAULT",
      document_id: n.document_id,
      description: n.description || null,
    });
  }
  return out;
}

function computeComponents(nodes, links) {
  const parent = new Map(nodes.map((n) => [n.id, n.id]));
  const find = (x) => {
    while (parent.get(x) !== x) {
      parent.set(x, parent.get(parent.get(x)));
      x = parent.get(x);
    }
    return x;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  for (const l of links) {
    const s = l.source?.id ?? l.source;
    const t = l.target?.id ?? l.target;
    if (parent.has(s) && parent.has(t)) union(s, t);
  }
  const compId = new Map();
  for (const n of nodes) compId.set(n.id, find(n.id));
  return compId;
}

/* ═══════════════════════════════════════════════════════════════
   GRAPH SEARCH — Command palette (⌘K)
   A floating trigger pill on the graph opens a centered overlay
   with category browsing and text search.
   ═══════════════════════════════════════════════════════════════ */
const formatType = (type) =>
  (type || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const SearchTrigger = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute left-1/2 top-[72px] z-50 flex -translate-x-1/2 items-center gap-2.5
               rounded-xl border border-surfaceBorder bg-innerBg/80 px-4 py-2 font-poppins
               shadow-md backdrop-blur-md transition-all duration-200
               hover:border-velBlue/30 hover:bg-innerBg/95 hover:shadow-lg
               active:scale-[0.98]"
  >
    <Search size={14} className="text-mutedText/60" strokeWidth={2} />
    <span className="text-[12px] text-mutedText/50">Search nodes…</span>
  </button>
);

const GraphSearch = ({ nodes, onSelectNode, clickedNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeType, setActiveType] = useState(null);
  const inputRef = useRef(null);

  // Auto-focus input when overlay opens, reset state on close.
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setQuery("");
      setActiveType(null);
    }
  }, [isOpen]);

  const typeEntries = useMemo(() => {
    if (!nodes?.length) return [];
    const counts = {};
    for (const n of nodes) {
      const t = n.iconType === "_DEFAULT" ? null : n.iconType;
      if (!t) continue;
      counts[t] = (counts[t] || 0) + 1;
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({
        type,
        count,
        Icon: ICON_MAP[type] || FALLBACK_ICON,
      }));
  }, [nodes]);

  const results = useMemo(() => {
    if (!nodes?.length) return [];
    const q = query.toLowerCase().trim();
    return nodes
      .filter((n) => {
        if (activeType && n.iconType !== activeType) return false;
        if (q && !n.name.toLowerCase().includes(q)) return false;
        return true;
      })
      .slice(0, 60);
  }, [nodes, query, activeType]);

  const showNodeResults = query.length > 0 || activeType;

  const handleSelect = (node) => {
    setIsOpen(false);
    onSelectNode(node);
  };

  const handleTypeClick = (type) => {
    setActiveType(type);
    setQuery("");
    inputRef.current?.focus();
  };

  return (
    <>
      <SearchTrigger onClick={() => setIsOpen(true)} />

      {isOpen && (
        <div
          className="absolute inset-0 z-50 flex items-start justify-center font-poppins"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-mainBg/60 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div className="relative mt-[12%] flex w-full max-w-[480px] flex-col overflow-hidden rounded-2xl border border-surfaceBorder bg-innerBg shadow-2xl">
            {/* Input */}
            <div className="flex items-center gap-3 border-b border-surfaceBorder px-4 py-3">
              <Search
                size={16}
                className="shrink-0 text-velBlue/60"
                strokeWidth={2}
              />

              {activeType && (
                <button
                  onClick={() => setActiveType(null)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md border border-velBlue/30 bg-velBlue/10 px-2 py-0.5 text-[10px] font-medium text-velBlue transition-colors hover:bg-velBlue/20"
                >
                  {(() => {
                    const Ic = ICON_MAP[activeType] || FALLBACK_ICON;
                    return <Ic size={10} strokeWidth={2} />;
                  })()}
                  {formatType(activeType)}
                  <X size={9} strokeWidth={2.5} className="opacity-60" />
                </button>
              )}

              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={activeType ? "Filter by name…" : "Search nodes…"}
                className="min-w-0 flex-1 bg-transparent text-[13px] text-mainText placeholder:text-mutedText/40 outline-none"
              />

              <button
                className="shrink-0 rounded-md p-1 text-mutedText/30 transition-colors hover:text-mutedText/70"
                onClick={() => setIsOpen(false)}
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>

            {/* Body */}
            <div className="max-h-[380px] overflow-y-auto">
              {showNodeResults ? (
                <>
                  <div className="sticky top-0 z-10 border-b border-surfaceBorder/50 bg-innerBg px-4 py-1.5">
                    <span className="text-[10px] font-medium tracking-wider text-mutedText/40">
                      {results.length} RESULT{results.length !== 1 ? "S" : ""}
                      {activeType && (
                        <span className="text-velBlue/50">
                          {" · "}
                          {formatType(activeType)}
                        </span>
                      )}
                    </span>
                  </div>

                  {results.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-10">
                      <Search
                        size={20}
                        className="text-mutedText/20"
                        strokeWidth={1.5}
                      />
                      <p className="text-[12px] text-mutedText/40">
                        No matching nodes
                      </p>
                    </div>
                  ) : (
                    <div className="py-1">
                      {results.map((node) => {
                        const IconComp =
                          ICON_MAP[node.iconType] || FALLBACK_ICON;
                        const isSelected = clickedNode?.id === node.id;
                        return (
                          <button
                            key={node.id}
                            onClick={() => handleSelect(node)}
                            className={`group flex w-full items-center gap-3 px-4 py-2 text-left transition-colors ${
                              isSelected
                                ? "bg-velBlue/[0.08]"
                                : "hover:bg-velBlue/[0.04]"
                            }`}
                          >
                            <div
                              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
                                isSelected
                                  ? "border border-velBlue/25 bg-velBlue/15"
                                  : "border border-transparent bg-surfaceBorder/30 group-hover:border-velBlue/20 group-hover:bg-velBlue/[0.06]"
                              }`}
                            >
                              <IconComp
                                size={14}
                                strokeWidth={2}
                                className={
                                  isSelected
                                    ? "text-velBlue"
                                    : "text-mutedText/50 group-hover:text-velBlue/70"
                                }
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-[12px] font-medium leading-snug text-mainText">
                                {node.name}
                              </p>
                            </div>
                            {node.iconType !== "_DEFAULT" && (
                              <span className="shrink-0 text-[10px] text-mutedText/30">
                                {formatType(node.iconType)}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="px-4 py-2">
                    <span className="text-[10px] font-medium tracking-wider text-mutedText/40">
                      BROWSE BY TYPE
                    </span>
                  </div>

                  {typeEntries.length === 0 ? (
                    <div className="px-4 py-8 text-center text-[12px] text-mutedText/40">
                      No categories
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-px bg-surfaceBorder/20 px-px pb-px">
                      {typeEntries.map(({ type, count, Icon }) => (
                        <button
                          key={type}
                          onClick={() => handleTypeClick(type)}
                          className="group flex flex-col items-center gap-1.5 bg-innerBg px-2 py-3 text-center transition-colors hover:bg-velBlue/[0.05]"
                        >
                          <div className="flex h-9 w-9 items-center justify-center rounded-full border border-surfaceBorder bg-mainBg transition-all group-hover:border-velBlue/30 group-hover:bg-velBlue/[0.06] group-hover:shadow-sm">
                            <Icon
                              size={18}
                              strokeWidth={1.8}
                              className="text-mutedText/50 transition-colors group-hover:text-velBlue"
                            />
                          </div>
                          <div>
                            <p className="text-[10px] font-medium leading-tight text-mainText/70 group-hover:text-mainText">
                              {formatType(type)}
                            </p>
                            <p className="text-[9px] text-mutedText/30">
                              {count} {count === 1 ? "node" : "nodes"}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════
   TOOLTIP — only shows on hover, not on click-selection
   ═══════════════════════════════════════════════════════════════ */
const NodeTooltip = ({ node, pos, bounds, links }) => {
  if (!node || !pos) return null;

  const maxW = bounds?.width ?? 900;
  const maxH = bounds?.height ?? 600;
  const typeLabel = (node.iconType === "_DEFAULT" ? "" : node.iconType || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  const allLinks = (links || []).filter((l) => {
    const s = l.source?.id ?? l.source;
    const t = l.target?.id ?? l.target;
    return s === node.id || t === node.id;
  });

  const outgoing = allLinks
    .filter((l) => (l.source?.id ?? l.source) === node.id)
    .slice(0, 4)
    .map((l) => ({
      label: (l.label || "").replace(/_/g, " "),
      name: l.target?.name ?? l.target,
    }));

  const incoming = allLinks
    .filter((l) => (l.target?.id ?? l.target) === node.id)
    .slice(0, 4)
    .map((l) => ({
      label: (l.label || "").replace(/_/g, " "),
      name: l.source?.name ?? l.source,
    }));

  const tipW = 280;
  const tipH = 200;

  const centerX = maxW / 2;
  const centerY = maxH / 2;
  const goLeft = pos.x > centerX;
  const goUp = pos.y > centerY;

  const left = Math.max(
    8,
    Math.min(goLeft ? pos.x - tipW - 20 : pos.x + 30, maxW - tipW - 8),
  );
  const top = Math.max(
    8,
    Math.min(goUp ? pos.y - tipH - 10 : pos.y + 30, maxH - tipH - 8),
  );

  return (
    <div
      className="pointer-events-none absolute z-40 w-[280px] overflow-hidden rounded-lg border border-velBlue/30 border-t-2 border-t-velBlue/50 bg-innerBg shadow-login-panel backdrop-blur-md transition-opacity duration-75"
      style={{ left, top }}
    >
      <div className="px-3 py-2.5">
        <p className="break-all text-[13px] font-medium leading-snug text-mainText line-clamp-3">
          {node.name}
        </p>

        {typeLabel && (
          <span className="mt-1.5 inline-block rounded-full bg-velBlue/10 px-2 py-[3px] text-[10px] font-medium tracking-wide text-velBlue">
            {typeLabel}
          </span>
        )}

        {outgoing.length > 0 && (
          <div className="mt-2 border-t border-surfaceBorder pt-2">
            <span className="text-[9px] font-medium tracking-wide text-mutedText/60">
              CONNECTS TO
            </span>
            {outgoing.map((c, i) => (
              <div
                key={i}
                className="mt-0.5 flex items-baseline gap-1.5 text-[11px] leading-relaxed"
              >
                <span className="shrink-0 text-velBlue/40">→</span>
                <span className="shrink-0 font-medium text-velBlue/70">
                  {c.label}
                </span>
                <span className="truncate text-mutedText">{c.name}</span>
              </div>
            ))}
          </div>
        )}

        {incoming.length > 0 && (
          <div className="mt-2 border-t border-surfaceBorder pt-2">
            <span className="text-[9px] font-medium tracking-wide text-mutedText/60">
              REFERENCED BY
            </span>
            {incoming.map((c, i) => (
              <div
                key={i}
                className="mt-0.5 flex items-baseline gap-1.5 text-[11px] leading-relaxed"
              >
                <span className="shrink-0 text-velBlue/40">←</span>
                <span className="shrink-0 font-medium text-velBlue/70">
                  {c.label}
                </span>
                <span className="truncate text-mutedText">{c.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EmptyState = () => (
  <div className="flex h-full w-full flex-col items-center justify-center gap-3 font-poppins">
    <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-velBlue/20 bg-velBlue/[0.06]">
      <Network size={28} className="text-velBlue" strokeWidth={1.4} />
    </div>
    <div className="flex flex-col items-center gap-1 text-center">
      <p className="text-[13px] font-medium tracking-tight text-mainText">
        No graph data yet
      </p>
    </div>
    <div className="mt-1 h-[1.5px] w-16 rounded-full bg-velBlue/40" />
  </div>
);

const GraphLoading = () => (
  <div className="flex h-full w-full flex-col items-center justify-center gap-2.5 font-poppins">
    <Loader2 size={22} className="animate-spin text-velBlue" strokeWidth={2} />
    <span className="text-[11px] text-mutedText">Building graph…</span>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   MAIN GRAPH
   ═══════════════════════════════════════════════════════════════ */
const MainGraph = (props) => {
  const { setRightComponent, setIsRightOpen, setRightSidebarMode } =
    useRightSidebar();

  // Tracks which graph source is active (my_graph, global, team member).
  // Starts null — waits briefly for a sidebar event before defaulting to my_graph.
  const [graphSource, setGraphSource] = useState(null);

  const fgRef = useRef();
  const divRef = useRef(null);
  const [graphData, setGraphData] = useState(props.signalData);
  const [isLoading, setIsLoading] = useState(true);
  const [isGraphEmpty, setIsGraphEmpty] = useState(false);
  const [stopEngine, setStopEngine] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // hoveredNode: transient mouse-over, cleared on mouseout.
  // clickedNode: persistent selection via click or search-result pick.
  // activeNode:  drives canvas highlighting — hover takes visual priority.
  const [hoveredNode, setHoveredNode] = useState(null);
  const [clickedNode, setClickedNode] = useState(null);
  const activeNode = hoveredNode || clickedNode;

  // Tooltip only shows on hover, not on click-selection (sidebar handles that).
  const [tooltipPos, setTooltipPos] = useState(null);

  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const el = document.documentElement;
    const update = () => setIsDark(el.classList.contains("dark"));
    const obs = new MutationObserver(update);
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    update();
    return () => obs.disconnect();
  }, []);

  const iconCacheRef = useRef(null);
  const themeRef = useRef(getCanvasTheme(false));
  const activeRef = useRef(null);

  if (!iconCacheRef.current) iconCacheRef.current = buildIconCache();

  useEffect(() => {
    themeRef.current = getCanvasTheme(isDark);
  }, [isDark]);

  useEffect(() => {
    activeRef.current = activeNode;
  }, [activeNode]);

  // Tooltip position tracks hovered node only.
  useEffect(() => {
    if (hoveredNode && fgRef.current) {
      setTooltipPos(
        fgRef.current.graph2ScreenCoords(hoveredNode.x, hoveredNode.y),
      );
    } else {
      setTooltipPos(null);
    }
  }, [hoveredNode]);

  const measure = useCallback(() => {
    if (!divRef.current) return;
    const r = divRef.current.getBoundingClientRect();
    setDimensions({
      width: Math.max(0, Math.floor(r.width)),
      height: Math.max(0, Math.floor(r.height)),
    });
  }, []);

  useLayoutEffect(() => {
    measure();
    if (!divRef.current) return;
    const ro = new ResizeObserver(measure);
    ro.observe(divRef.current);
    return () => ro.disconnect();
  }, [measure]);

  useEffect(() => {
    measure();
    const t1 = setTimeout(measure, 150);
    const t2 = setTimeout(measure, 400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [
    props.isLeftOpen,
    props.isRightOpen,
    props.rightWidth,
    props.LEFT_WIDTH,
    measure,
  ]);

  useEffect(() => {
    if (!fgRef.current) return;
    fgRef.current.d3ReheatSimulation();
    if (!props.isLeftOpen && !props.isRightOpen) {
      requestAnimationFrame(() => fgRef.current?.zoomToFit(400, 24));
    }
  }, [
    dimensions.width,
    dimensions.height,
    props.isLeftOpen,
    props.isRightOpen,
  ]);

  const createGraph = useCallback((data) => {
    if (!data?.nodes?.length && !data?.links?.length) {
      setIsGraphEmpty(true);
      setIsLoading(false);
      return;
    }
    setIsGraphEmpty(false);
    setGraphData({ nodes: buildNodes(data.nodes), links: data.links });
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (props.navbarControlsData) createGraph(props.navbarControlsData);
  }, [props.navbarControlsData, createGraph]);

  // Fetch graph data based on the active source.
  const fetchGraphForSource = useCallback(
    (source) => {
      setIsGraphEmpty(false);
      setIsLoading(true);
      setStopEngine(false);
      setClickedNode(null);
      setIsRightOpen(false);

      let fetchPromise;
      if (source.kind === "global") {
        fetchPromise = getGlobalGraph();
      } else if (source.kind === "team" && source.uuid) {
        fetchPromise = getTeamMemberGraph(source.uuid);
      } else {
        // Default: my graph
        fetchPromise = getGraphEdges(props.currentCompany);
      }

      fetchPromise
        .then((r) => createGraph(r.data))
        .catch((e) => {
          console.error(e);
          setIsLoading(false);
        });
    },
    [props.currentCompany, createGraph, setIsRightOpen],
  );

  // Single effect for fetching — fires on mount, source change, or company change.
  const lastFetchRef = useRef(null);
  useEffect(() => {
    // Skip if source hasn't been determined yet (waiting for sidebar event).
    if (!graphSource) return;

    // Deduplicate: skip if source + company haven't actually changed.
    const key = `${graphSource.kind}:${graphSource.uuid || ""}:${props.currentCompany}`;
    if (lastFetchRef.current === key) return;
    lastFetchRef.current = key;

    fetchGraphForSource(graphSource);

    // Notify sidebar which source is active.
    window.dispatchEvent(
      new CustomEvent("graph:active-source-changed", { detail: graphSource }),
    );
  }, [graphSource, props.currentCompany, fetchGraphForSource]);

  // Listen for sidebar source-switch events. Guard against setting the same
  // source twice (which would be a no-op anyway thanks to the dedup above).
  useEffect(() => {
    const handler = (e) => {
      if (!e.detail) return;
      setGraphSource((prev) => {
        const next = e.detail;
        if (prev && prev.kind === next.kind && prev.uuid === next.uuid)
          return prev;
        return next;
      });
    };
    window.addEventListener("sidebar:graph-switch-source", handler);
    return () =>
      window.removeEventListener("sidebar:graph-switch-source", handler);
  }, []);

  // If no sidebar event sets the source within 600ms of mount, default to
  // my_graph. This covers direct navigation to /graph without a sidebar click.
  // The sidebar retries at 50/200/500ms, so 600ms guarantees they arrive first.
  useEffect(() => {
    const t = setTimeout(() => {
      setGraphSource((prev) => prev ?? { kind: "my_graph" });
    }, 600);
    return () => clearTimeout(t);
  }, []);

  /* ═══════════ CANVAS RENDERING ═══════════ */
  const nodeCanvasObject = useCallback(
    (node, ctx, globalScale) => {
      const cache = iconCacheRef.current;
      const tc = themeRef.current;
      if (!tc) return;

      const active = activeRef.current;
      const isActive = active?.id === node.id;

      const isNeighbour =
        active &&
        !isActive &&
        graphData?.links?.some((l) => {
          const s = l.source?.id ?? l.source;
          const t = l.target?.id ?? l.target;
          return (
            (s === active.id && t === node.id) ||
            (t === active.id && s === node.id)
          );
        });
      const dimmed = active && !isActive && !isNeighbour;

      const zoomFactor = Math.max(
        0.55,
        Math.min(1.4, 1 / Math.sqrt(globalScale)),
      );
      const R = (isActive ? 13 : 9) * zoomFactor;
      const ICON = (isActive ? 17 : 12) * zoomFactor;

      ctx.save();
      if (dimmed) ctx.globalAlpha = 0.25;

      ctx.beginPath();
      ctx.arc(node.x, node.y, R, 0, Math.PI * 2);
      ctx.fillStyle = isActive ? tc.nodeHoverFill : tc.nodeFill;
      ctx.fill();
      ctx.strokeStyle =
        isActive || isNeighbour ? tc.nodeHoverStroke : tc.nodeStroke;
      ctx.lineWidth = isActive ? 0.8 : isNeighbour ? 0.5 : 0.3;
      ctx.stroke();

      const img = cache[node.iconType] || cache._DEFAULT;
      if (img.complete && img.naturalWidth > 0) {
        ctx.drawImage(img, node.x - ICON / 2, node.y - ICON / 2, ICON, ICON);
      } else {
        ctx.beginPath();
        ctx.arc(node.x, node.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = tc.velBlue;
        ctx.fill();
      }

      if (globalScale < 0.4) {
        ctx.restore();
        return;
      }

      const rawLabel = node.name;
      const maxChars = globalScale < 0.8 ? 16 : MAX_LABEL_CHARS;
      const label =
        rawLabel.length > maxChars
          ? rawLabel.slice(0, maxChars - 2) + "…"
          : rawLabel;
      const fs = Math.min(3.5, Math.max(2.2, 14 / globalScale));
      ctx.font = `500 ${fs}px "Poppins", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";

      const tw = ctx.measureText(label).width;
      const px = 2.8;
      const py = 1.2;
      const lx = node.x - tw / 2 - px;
      const ly = node.y + R + 2.5;
      const lw = tw + px * 2;
      const lh = fs + py * 2;
      const cr = 2;

      ctx.beginPath();
      ctx.moveTo(lx + cr, ly);
      ctx.lineTo(lx + lw - cr, ly);
      ctx.quadraticCurveTo(lx + lw, ly, lx + lw, ly + cr);
      ctx.lineTo(lx + lw, ly + lh - cr);
      ctx.quadraticCurveTo(lx + lw, ly + lh, lx + lw - cr, ly + lh);
      ctx.lineTo(lx + cr, ly + lh);
      ctx.quadraticCurveTo(lx, ly + lh, lx, ly + lh - cr);
      ctx.lineTo(lx, ly + cr);
      ctx.quadraticCurveTo(lx, ly, lx + cr, ly);
      ctx.closePath();
      ctx.fillStyle = tc.labelBg;
      ctx.fill();
      ctx.strokeStyle = tc.labelStroke;
      ctx.lineWidth = 0.15;
      ctx.stroke();

      ctx.fillStyle = isActive ? tc.labelHoverText : tc.labelText;
      ctx.fillText(label, node.x, ly + py);

      ctx.restore();
    },
    [graphData],
  );

  const linkColorFn = useCallback(
    (link) => {
      const tc = themeRef.current;
      if (!tc) return "transparent";
      if (!activeNode) return tc.link;
      const s = link.source?.id ?? link.source;
      const t = link.target?.id ?? link.target;
      return s === activeNode.id || t === activeNode.id
        ? tc.linkHover
        : tc.link;
    },
    [activeNode],
  );

  const linkWidthFn = useCallback(
    (link) => {
      if (!activeNode) return isDark ? 1.2 : 1;
      const s = link.source?.id ?? link.source;
      const t = link.target?.id ?? link.target;
      return s === activeNode.id || t === activeNode.id ? 2.5 : 0.4;
    },
    [activeNode, isDark],
  );

  const particleWidthFn = useCallback(
    (link) => {
      if (!activeNode) return 0;
      const s = link.source?.id ?? link.source;
      const t = link.target?.id ?? link.target;
      return s === activeNode.id || t === activeNode.id ? 2.5 : 0;
    },
    [activeNode],
  );

  /* ═══════════ SIDEBAR ═══════════ */
  const openArticlePanel = useCallback(
    (docId, article) => {
      const dark = document.documentElement.classList.contains("dark");
      setRightComponent(
        <ArticleDetailPanel
          key={docId}
          article={article}
          cardId={docId}
          t={getTheme(dark)}
        />,
      );
    },
    [setRightComponent],
  );

  // Shared logic: select a node, center+zoom, and open its doc in the sidebar.
  // Used by both direct canvas clicks and search-result picks.
  const selectNode = useCallback(
    (node, { toggle = false } = {}) => {
      if (toggle && clickedNode?.id === node.id) {
        setClickedNode(null);
        setIsRightOpen(false);
        return;
      }

      setClickedNode(node);

      // Smoothly center on the node. Only zoom in if we're currently
      // zoomed out — never zoom out to reach the target. This avoids
      // the jarring zoom-out-then-in whiplash.
      const fg = fgRef.current;
      if (fg) {
        fg.centerAt(node.x, node.y, 600);
        const current = fg.zoom();
        const target = Math.max(current, 2.5);
        if (target > current) {
          fg.zoom(target, 600);
        }
      }

      if (node.document_id) {
        setRightSidebarMode(null);
        setIsRightOpen(true);
        setRightComponent(
          <div className="p-4 text-[13px] text-zinc-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>,
        );

        getDocIdData(node.document_id)
          .then((r) => {
            const a = r?.data;
            if (!a) return;
            openArticlePanel(node.document_id, {
              ...a,
              like: !!a.like,
              dislike: !!a.dislike,
              catalog: !!a.catalog,
            });
          })
          .catch(() =>
            setRightComponent(
              <div className="p-6 text-center text-[13px] text-zinc-500">
                Unable to load source
              </div>,
            ),
          );
      }
    },
    [
      clickedNode,
      setRightComponent,
      setIsRightOpen,
      setRightSidebarMode,
      openArticlePanel,
    ],
  );

  const handleNodeClick = useCallback(
    (node) => selectNode(node, { toggle: true }),
    [selectNode],
  );

  // Search results call selectNode without toggle — always selects.
  const handleSearchSelect = useCallback(
    (node) => selectNode(node),
    [selectNode],
  );

  const handleNodeHover = useCallback((node) => {
    setHoveredNode(node || null);
  }, []);

  const handleBackgroundClick = useCallback(() => {
    setClickedNode(null);
    setIsRightOpen(false);
  }, [setIsRightOpen]);

  const handleDrag = useCallback((node) => {
    node.fx = node.x;
    node.fy = node.y;
  }, []);

  const handleEngineStop = useCallback(() => {
    if (!stopEngine) {
      setStopEngine(true);
    }
  }, [stopEngine]);

  // Zoom into the graph shortly after data loads.
  useEffect(() => {
    if (isLoading || isGraphEmpty || !graphData?.nodes?.length) return;

    const t = setTimeout(() => {
      fgRef.current?.zoomToFit(500, -60);
    }, 600);

    return () => clearTimeout(t);
  }, [isLoading, isGraphEmpty, graphData]);

  /* ═══════════ D3 FORCES ═══════════ */
  const d3Init = useCallback(
    (sim) => {
      const nodes = graphData?.nodes || [];
      const links = graphData?.links || [];
      const n = nodes.length;
      const isLarge = n > 100;

      sim.force(
        "charge",
        d3
          .forceManyBody()
          .strength(isLarge ? -600 : -1000)
          .theta(0.9),
      );

      sim.force(
        "link",
        d3
          .forceLink()
          .id((d) => d.id)
          .distance(LINK_DISTANCE)
          .strength(0.3),
      );

      sim.force(
        "collide",
        d3
          .forceCollide()
          .radius((d) => {
            const chars = Math.min((d.name || "").length, MAX_LABEL_CHARS);
            return Math.max(
              chars * COLLIDE_PER_CHAR + COLLIDE_PADDING,
              COLLIDE_MIN,
            );
          })
          .strength(1)
          .iterations(4),
      );

      const compId = computeComponents(nodes, links);
      const comps = Array.from(new Set(compId.values()));
      const cols = Math.max(1, Math.ceil(Math.sqrt(comps.length)));
      const rows = Math.max(1, Math.ceil(comps.length / cols));
      const spacing = LINK_DISTANCE * 0.6;
      const anchors = new Map();
      comps.forEach((cid, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        anchors.set(cid, {
          x: (col - (cols - 1) / 2) * spacing,
          y: (row - (rows - 1) / 2) * spacing,
        });
      });
      sim.force(
        "componentX",
        d3.forceX((d) => anchors.get(compId.get(d.id))?.x ?? 0).strength(0.03),
      );
      sim.force(
        "componentY",
        d3.forceY((d) => anchors.get(compId.get(d.id))?.y ?? 0).strength(0.03),
      );

      sim.force("center", d3.forceCenter(0, 0).strength(1));

      sim.velocityDecay(0.4);
      sim.alphaDecay(0.015);
      return sim;
    },
    [graphData],
  );

  const linkCanvasObject = useCallback((link, ctx, globalScale) => {
    const tc = themeRef.current;
    if (!tc || !link.label) return;
    if (globalScale < 1.5) return;

    const sx = link.source.x,
      sy = link.source.y;
    const tx = link.target.x,
      ty = link.target.y;
    const mx = (sx + tx) / 2;
    const my = (sy + ty) / 2;

    const active = activeRef.current;
    const isHighlighted =
      active &&
      ((link.source.id ?? link.source) === active.id ||
        (link.target.id ?? link.target) === active.id);

    const fs = Math.max(2, 8 / globalScale);
    ctx.font = `400 ${fs}px "Poppins", sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const text = link.label.replace(/_/g, " ");
    const tw = ctx.measureText(text).width;

    const px = 2,
      py = 0.8;
    ctx.fillStyle = tc.labelBg;
    ctx.fillRect(mx - tw / 2 - px, my - fs / 2 - py, tw + px * 2, fs + py * 2);

    ctx.fillStyle = isHighlighted ? tc.labelHoverText : tc.labelText;
    ctx.globalAlpha = isHighlighted ? 0.9 : 0.6;
    ctx.fillText(text, mx, my);
    ctx.globalAlpha = 1;
  }, []);

  /* ═══════════ RENDER ═══════════ */
  return (
    <div
      ref={divRef}
      className="relative flex h-full w-full flex-col overflow-hidden rounded-lg border border-surfaceBorder bg-mainBg font-poppins"
    >
      {isGraphEmpty ? (
        <EmptyState />
      ) : isLoading ? (
        <GraphLoading />
      ) : (
        <>
          {/* Search command palette */}
          <GraphSearch
            nodes={graphData?.nodes}
            onSelectNode={handleSearchSelect}
            clickedNode={clickedNode}
          />

          {/* Tooltip — hover only, hidden when a node is click-selected */}
          <NodeTooltip
            node={hoveredNode}
            pos={tooltipPos}
            bounds={dimensions}
            links={graphData?.links}
          />

          <div className="h-full w-full">
            <ForceGraph2D
              ref={fgRef}
              graphData={graphData}
              backgroundColor="rgba(0,0,0,0)"
              nodeId="id"
              nodeLabel={() => ""}
              autoPauseRedraw={false}
              width={dimensions.width}
              height={dimensions.height}
              minZoom={0.2}
              maxZoom={8}
              d3ForceInit={d3Init}
              onNodeClick={handleNodeClick}
              onNodeHover={handleNodeHover}
              onNodeDrag={handleDrag}
              onNodeDragEnd={handleDrag}
              onBackgroundClick={handleBackgroundClick}
              nodeCanvasObject={nodeCanvasObject}
              onEngineStop={handleEngineStop}
              cooldownTicks={Infinity}
              linkColor={linkColorFn}
              linkWidth={linkWidthFn}
              linkDirectionalParticles={3}
              linkDirectionalParticleColor={() =>
                themeRef.current?.particle || VELBLUE
              }
              linkDirectionalParticleWidth={particleWidthFn}
              linkDirectionalParticleSpeed={() => 0.006}
              linkLineDash={(link) => (!link.current_case ? [2, 1] : null)}
              linkCanvasObjectMode={() => "after"}
              linkCanvasObject={linkCanvasObject}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default MainGraph;
